import { useState } from 'react';

import {
  IconArrowUp,
  IconCalendarOutline,
  IconCheckDefault,
  IconStarOutline,
} from '@iwoca/icons';
import { Card } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import Rive from '@rive-app/react-canvas';
import { clsx } from 'clsx';

import styles from './CheckEligibilityDE.module.css';
// @ts-expect-error does not recognise riv import.
import successTick from '../../../assets/animation/successTick.riv';
import { calculateEligibleAmount } from '../../../helpers/calculateEligibleAmount';
import { calculateTimeTrading } from '../../../helpers/calculateTimeTrading';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../lendingApi/lendingApiQueries';
import { TGetStateResponse } from '../../../types';
import { Button } from '../../Button/Button';
import { normaliseStepDataDE } from '../../helpers/stateNormalisation';
import { CHECK_ELIGIBILITY_STEP_ID } from '../../helpers/steps';

const CheckEligibilityDE = () => {
  const [isNavigating, setIsNavigating] = useState(false);

  const { formatCurrency } = useNumberFormat();
  const { navigateSignupDE } = useSignupRouterDE();

  const { isStateFetching } = useGetStateQuery<TGetStateResponse>();
  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepDataDE(data)[CHECK_ELIGIBILITY_STEP_ID],
  );
  const stateKey = useContextStateKey();

  const { application } = useGetApplicationQuery();

  const requestedAmount = application?.requested_products?.[0].amount;

  const eligibleAmount = calculateEligibleAmount({
    annualTurnover: normalisedState.last12MonthsTurnover || 0,
    requestedAmount,
  });

  const formattedEligibleAmount = formatCurrency(eligibleAmount);

  const { years: yearsTrading, months: monthsTrading } = calculateTimeTrading(
    normalisedState.startDateMonth,
    normalisedState.startDateYear,
  );

  const navigateToTradingHistory = async () => {
    setIsNavigating(true);

    await navigateSignupDE('schritt-3', stateKey);

    setIsNavigating(false);
  };

  return (
    <section className={styles.checkEligibilityContainer}>
      <div className={styles.animationContainer}>
        <Rive
          src={successTick}
          className={styles.accountSuccessTickRive}
          data-testid="success-tick-animation"
        />
      </div>
      <h1 className={styles.title}>
        <Trans>Good news, you’re eligible!</Trans>
      </h1>
      <div className={styles.checksContainer}>
        <div className={styles.check}>
          <IconCheckDefault className={styles.checkIcon} />
          {yearsTrading === 0 ? (
            <p>
              <Trans>Trading for {monthsTrading} months</Trans>
            </p>
          ) : (
            <p>
              <Trans>Trading for {yearsTrading} years</Trans>
            </p>
          )}
        </div>
        <div className={styles.check}>
          <IconCheckDefault className={styles.checkIcon} />
          <p>
            <Trans>Company is active</Trans>
          </p>
        </div>
        <div className={styles.check}>
          <IconCheckDefault className={styles.checkIcon} />
          <p>
            <Trans>Personal credit check</Trans>
          </p>
        </div>
      </div>

      <Card className={clsx(styles.benefitsCard)}>
        <h2>
          <Trans>
            Your business could borrow up to {formattedEligibleAmount}* today
          </Trans>
        </h2>
        <p className={styles.benefitsSummary}>
          <Trans>
            Bridge gaps, grab opportunities or just keep cash flow smooth. You
            only pay interest on the amount you withdraw. Funds in your account
            within 24 hours.
          </Trans>
        </p>
        <section className={styles.benefitsContainer}>
          <div className={styles.benefit}>
            <IconCalendarOutline className={styles.benefitIcon} />
            <p>
              <Trans>Borrow from 1 day to 60 months</Trans>
            </p>
          </div>
          <div className={styles.benefit}>
            <IconStarOutline className={styles.benefitIcon} />
            <p>
              <Trans>Repay early for free at any time</Trans>
            </p>
          </div>

          <div className={styles.benefit}>
            <IconArrowUp className={styles.benefitIcon} />
            <p>
              <Trans>Increases in credit possible</Trans>
            </p>
          </div>
        </section>
        <p className={styles.terms}>
          <small>
            <Trans>*Subject to approval</Trans>
          </small>
        </p>
      </Card>
      <Button
        className={styles.nextButton}
        disabled={isStateFetching}
        loading={isNavigating}
        type="submit"
        onClick={() => navigateToTradingHistory()}
      >
        <Trans>Next, add Trading history</Trans>
      </Button>
    </section>
  );
};

export default CheckEligibilityDE;
